import { useState, useRef, useCallback, useEffect } from 'react'
import type { Point } from '../../types'
import { LivePlayer } from '../../lib/mseLivePlayer'

type EditorMode = 'polygon' | 'line' | 'rect'

interface Props {
  /** Camera ID to stream live feed */
  cameraId: string
  /** Supabase auth token for WS proxy */
  authToken: string
  /** Drawing mode */
  mode: EditorMode
  /** Existing regions — polygons rendered as overlays */
  polygons?: { id: number; points: Point[]; color?: string }[]
  /** Existing lines rendered as overlays */
  lines?: { id: number; startX: number; startY: number; endX: number; endY: number; color?: string }[]
  /** Existing rectangles (0-100 percentage coords) */
  rects?: { id: number; x: number; y: number; w: number; h: number; color?: string }[]
  /** Called when a new polygon is completed (points in 0-100 % coords) */
  onPolygonComplete?: (points: Point[]) => void
  /** Called when a new line is completed */
  onLineComplete?: (start: Point, end: Point) => void
  /** Called when a new rect is completed */
  onRectComplete?: (x: number, y: number, w: number, h: number) => void
  /** Called to delete a region by id */
  onDelete?: (id: number) => void
}

export function ZoneEditor({
  cameraId, authToken, mode,
  polygons = [], lines = [], rects = [],
  onPolygonComplete, onLineComplete, onRectComplete, onDelete,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const feedRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<LivePlayer | null>(null)
  const [connected, setConnected] = useState(false)
  const [drawing, setDrawing] = useState<Point[]>([])
  const [mousePos, setMousePos] = useState<Point | null>(null)
  const [canvasSize, setCanvasSize] = useState({ w: 640, h: 360 })

  // Start live stream (always SD/MJPEG for zone drawing background)
  useEffect(() => {
    if (!authToken || !cameraId || !feedRef.current) return

    const container = feedRef.current
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const wsUrl = `${proto}://${location.host}/api/camera/ws-stream?cameraId=${encodeURIComponent(cameraId)}&streamId=2&token=${encodeURIComponent(authToken)}`

    const player = new LivePlayer({
      container,
      wsUrl,
      isHd: false,
      onConnected: () => setConnected(true),
      onError: () => setConnected(false),
    })
    player.start()
    playerRef.current = player

    return () => {
      player.stop()
      playerRef.current = null
    }
  }, [cameraId, authToken])

  // Size canvas when first frame loads
  useEffect(() => {
    const feed = feedRef.current
    if (!feed) return
    // The LivePlayer creates an <img> inside the feed div for SD mode
    const observer = new MutationObserver(() => {
      const imgEl = feed.querySelector('img')
      if (imgEl) {
        observer.disconnect()
        const onLoad = () => {
          const container = containerRef.current
          if (container && imgEl.naturalWidth > 0) {
            const maxW = container.clientWidth
            const ratio = imgEl.naturalHeight / imgEl.naturalWidth
            setCanvasSize({ w: maxW, h: Math.round(maxW * ratio) })
          }
        }
        imgEl.addEventListener('load', onLoad)
        // Check if already loaded
        if (imgEl.naturalWidth > 0) onLoad()
      }
    })
    observer.observe(feed, { childList: true })
    // Also check if img already exists
    const existing = feed.querySelector('img')
    if (existing) observer.disconnect()
    return () => observer.disconnect()
  }, [connected])

  // Convert pixel coords to percentage (0-100)
  const toPercent = useCallback((px: number, py: number): Point => ({
    x: (px / canvasSize.w) * 100,
    y: (py / canvasSize.h) * 100,
  }), [canvasSize])

  // Convert percentage to pixel
  const toPixel = useCallback((p: Point): { px: number; py: number } => ({
    px: (p.x / 100) * canvasSize.w,
    py: (p.y / 100) * canvasSize.h,
  }), [canvasSize])

  // Draw overlays on transparent canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = canvasSize.w
    canvas.height = canvasSize.h

    // Clear to transparent
    ctx.clearRect(0, 0, canvasSize.w, canvasSize.h)

    // If not connected yet, draw placeholder background
    if (!connected) {
      ctx.fillStyle = '#1a1a2e'
      ctx.fillRect(0, 0, canvasSize.w, canvasSize.h)
      ctx.fillStyle = '#666'
      ctx.font = '14px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Connecting to live feed...', canvasSize.w / 2, canvasSize.h / 2)
    }

    // Draw existing polygons
    for (const poly of polygons) {
      if (poly.points.length < 2) continue
      ctx.beginPath()
      const first = toPixel(poly.points[0])
      ctx.moveTo(first.px, first.py)
      for (let i = 1; i < poly.points.length; i++) {
        const p = toPixel(poly.points[i])
        ctx.lineTo(p.px, p.py)
      }
      ctx.closePath()
      ctx.fillStyle = (poly.color ?? 'rgba(220, 38, 38, 0.2)')
      ctx.fill()
      ctx.strokeStyle = poly.color ?? 'rgba(220, 38, 38, 0.8)'
      ctx.lineWidth = 2
      ctx.stroke()

      // Label
      const cx = poly.points.reduce((s, p) => s + p.x, 0) / poly.points.length
      const cy = poly.points.reduce((s, p) => s + p.y, 0) / poly.points.length
      const lp = toPixel({ x: cx, y: cy })
      ctx.fillStyle = 'white'
      ctx.font = 'bold 11px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(`Zone ${poly.id + 1}`, lp.px, lp.py)
    }

    // Draw existing lines
    for (let li = 0; li < lines.length; li++) {
    const line = lines[li]
      const s = toPixel({ x: line.startX, y: line.startY })
      const e = toPixel({ x: line.endX, y: line.endY })
      ctx.beginPath()
      ctx.moveTo(s.px, s.py)
      ctx.lineTo(e.px, e.py)
      ctx.strokeStyle = line.color ?? 'rgba(59, 130, 246, 0.9)'
      ctx.lineWidth = 3
      ctx.stroke()

      // Arrow head
      const angle = Math.atan2(e.py - s.py, e.px - s.px)
      const headLen = 12
      ctx.beginPath()
      ctx.moveTo(e.px, e.py)
      ctx.lineTo(e.px - headLen * Math.cos(angle - 0.4), e.py - headLen * Math.sin(angle - 0.4))
      ctx.moveTo(e.px, e.py)
      ctx.lineTo(e.px - headLen * Math.cos(angle + 0.4), e.py - headLen * Math.sin(angle + 0.4))
      ctx.stroke()

      // Label
      const mx = (s.px + e.px) / 2
      const my = (s.py + e.py) / 2
      ctx.fillStyle = 'white'
      ctx.font = 'bold 11px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(`Line ${li + 1}`, mx, my - 8)
    }

    // Draw existing rects
    for (const r of rects) {
      const tl = toPixel({ x: r.x, y: r.y })
      const br = toPixel({ x: r.x + r.w, y: r.y + r.h })
      ctx.strokeStyle = r.color ?? 'rgba(234, 179, 8, 0.8)'
      ctx.lineWidth = 2
      ctx.strokeRect(tl.px, tl.py, br.px - tl.px, br.py - tl.py)
      ctx.fillStyle = r.color ? r.color.replace('0.8', '0.15') : 'rgba(234, 179, 8, 0.15)'
      ctx.fillRect(tl.px, tl.py, br.px - tl.px, br.py - tl.py)
    }

    // Draw current drawing in progress
    if (drawing.length > 0) {
      ctx.beginPath()
      ctx.moveTo(drawing[0].x, drawing[0].y)
      for (let i = 1; i < drawing.length; i++) {
        ctx.lineTo(drawing[i].x, drawing[i].y)
      }
      if (mousePos) {
        ctx.lineTo(mousePos.x, mousePos.y)
      }
      if (mode === 'polygon' && drawing.length > 2) {
        ctx.closePath()
        ctx.fillStyle = 'rgba(16, 185, 129, 0.2)'
        ctx.fill()
      }
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.9)'
      ctx.lineWidth = 2
      ctx.setLineDash([5, 5])
      ctx.stroke()
      ctx.setLineDash([])

      // Draw points
      for (const p of drawing) {
        ctx.beginPath()
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2)
        ctx.fillStyle = '#10b981'
        ctx.fill()
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 1.5
        ctx.stroke()
      }
    }

    // Draw rect in progress
    if (mode === 'rect' && drawing.length === 1 && mousePos) {
      const x1 = drawing[0].x
      const y1 = drawing[0].y
      const w = mousePos.x - x1
      const h = mousePos.y - y1
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.9)'
      ctx.lineWidth = 2
      ctx.setLineDash([5, 5])
      ctx.strokeRect(x1, y1, w, h)
      ctx.setLineDash([])
      ctx.fillStyle = 'rgba(16, 185, 129, 0.15)'
      ctx.fillRect(x1, y1, w, h)
    }
  }, [connected, canvasSize, polygons, lines, rects, drawing, mousePos, mode, toPixel])

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (mode === 'line') {
      if (drawing.length === 0) {
        setDrawing([{ x, y }])
      } else {
        const start = toPercent(drawing[0].x, drawing[0].y)
        const end = toPercent(x, y)
        onLineComplete?.(start, end)
        setDrawing([])
      }
    } else if (mode === 'rect') {
      if (drawing.length === 0) {
        setDrawing([{ x, y }])
      } else {
        const p1 = toPercent(drawing[0].x, drawing[0].y)
        const p2 = toPercent(x, y)
        const rx = Math.min(p1.x, p2.x)
        const ry = Math.min(p1.y, p2.y)
        const rw = Math.abs(p2.x - p1.x)
        const rh = Math.abs(p2.y - p1.y)
        onRectComplete?.(rx, ry, rw, rh)
        setDrawing([])
      }
    } else {
      // Polygon mode — check if clicking near first point to close
      if (drawing.length >= 3) {
        const dist = Math.hypot(x - drawing[0].x, y - drawing[0].y)
        if (dist < 12) {
          const points = drawing.map(p => toPercent(p.x, p.y))
          onPolygonComplete?.(points)
          setDrawing([])
          return
        }
      }
      setDrawing([...drawing, { x, y }])
    }
  }, [drawing, mode, toPercent, onPolygonComplete, onLineComplete, onRectComplete])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
  }, [])

  const handleRightClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    // Cancel current drawing
    setDrawing([])
  }, [])

  return (
    <div ref={containerRef} className="relative" style={{ width: '100%', height: canvasSize.h }}>
      {/* Live feed background */}
      <div
        ref={feedRef}
        className="absolute inset-0 w-full h-full rounded-lg overflow-hidden"
        style={{ display: connected ? 'block' : 'none' }}
      />
      {/* Transparent canvas overlay for zone drawing */}
      <canvas
        ref={canvasRef}
        width={canvasSize.w}
        height={canvasSize.h}
        className="absolute inset-0 w-full h-full rounded-lg cursor-crosshair border border-gray-700"
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onContextMenu={handleRightClick}
      />
      {drawing.length > 0 && (
        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded">
          {mode === 'polygon' && `${drawing.length} points — click near first point to close, right-click to cancel`}
          {mode === 'line' && 'Click to set end point'}
          {mode === 'rect' && 'Click to set opposite corner'}
        </div>
      )}
      {drawing.length === 0 && (
        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded">
          Click to start drawing {mode === 'polygon' ? 'zone' : mode === 'line' ? 'line' : 'rectangle'}
        </div>
      )}

      {/* Delete buttons for existing regions */}
      {onDelete && (polygons.length > 0 || lines.length > 0) && (
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          {polygons.map(p => (
            <button
              key={`p-${p.id}`}
              onClick={() => onDelete(p.id)}
              className="bg-black/70 hover:bg-error/80 text-white text-[10px] px-2 py-1 rounded transition-colors"
            >
              Delete Zone {p.id + 1}
            </button>
          ))}
          {lines.map((l, idx) => (
            <button
              key={`l-${l.id}`}
              onClick={() => onDelete(l.id)}
              className="bg-black/70 hover:bg-error/80 text-white text-[10px] px-2 py-1 rounded transition-colors"
            >
              Delete Line {idx + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
